import { promises as fs } from "node:fs";
import { tmpdir } from "node:os";
import { join } from "node:path";
import { TIME_CONSTANTS } from "./constants";

export interface FxRate {
	/** USD -> EUR multiplier: eur = usd * rate. */
	rate: number;
	/** ISO date the rate is effective for (or when it was resolved). */
	asOf: string;
}

interface Logger {
	warn(message: string, ...args: unknown[]): void;
}

// Frankfurter (ECB reference rates): free, no API key, daily.
const FX_URL = "https://api.frankfurter.dev/v1/latest?base=USD&symbols=EUR";

interface FrankfurterResponse {
	amount?: number;
	base?: string;
	date?: string;
	rates?: { EUR?: number };
}

/**
 * Resolves the USD->EUR exchange rate, mirroring `PriceCatalogue`: fetch once
 * per process (then per refresh window), cache to disk for offline use, and
 * degrade gracefully. A fixed rate can be forced via `CF_USD_EUR_RATE` for
 * air-gapped deployments. On total failure the rate falls back to 1 (amounts
 * render unchanged as USD), which is honest rather than wrong.
 */
class FxCatalogue {
	private static instance: FxCatalogue;
	private cached: FxRate | null = null;
	private lastFetch = 0;
	private logger: Logger | null = null;

	private constructor() {}

	setLogger(logger: Logger): void {
		this.logger = logger;
	}

	static get(): FxCatalogue {
		if (!FxCatalogue.instance) {
			FxCatalogue.instance = new FxCatalogue();
		}
		return FxCatalogue.instance;
	}

	private getCachePath(): string {
		return join(tmpdir(), "ccflare", "fx-usd-eur.json");
	}

	private getCacheDurationMs(): number {
		const hours = Number(process.env.CF_FX_REFRESH_HOURS) || 24;
		return hours * TIME_CONSTANTS.HOUR;
	}

	/** A fixed rate from the environment short-circuits all network access. */
	private getOverrideRate(): number | null {
		const raw = process.env.CF_USD_EUR_RATE;
		if (!raw) return null;
		const value = Number(raw);
		return Number.isFinite(value) && value > 0 ? value : null;
	}

	private async loadFromCache(): Promise<FxRate | null> {
		try {
			const cachePath = this.getCachePath();
			const content = await fs.readFile(cachePath, "utf-8");
			const parsed = JSON.parse(content) as FxRate;
			return Number.isFinite(parsed.rate) && parsed.rate > 0 ? parsed : null;
		} catch {
			return null;
		}
	}

	private async saveToCache(data: FxRate): Promise<void> {
		try {
			const cachePath = this.getCachePath();
			await fs.mkdir(join(tmpdir(), "ccflare"), { recursive: true });
			await fs.writeFile(cachePath, JSON.stringify(data, null, 2));
		} catch (error) {
			this.logger?.warn("Failed to save fx cache: %s", error);
		}
	}

	private async fetchRemote(): Promise<FxRate | null> {
		try {
			const response = await fetch(FX_URL);
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}
			const data = (await response.json()) as FrankfurterResponse;
			const rate = data.rates?.EUR;
			if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0) {
				throw new Error("Missing EUR rate in response");
			}
			const result: FxRate = {
				rate,
				asOf: data.date ?? new Date().toISOString().slice(0, 10),
			};
			await this.saveToCache(result);
			return result;
		} catch (error) {
			this.logger?.warn("Failed to fetch fx rate: %s", error);
			return null;
		}
	}

	async getRate(): Promise<FxRate> {
		const override = this.getOverrideRate();
		if (override !== null) {
			return { rate: override, asOf: "fixed" };
		}

		if (
			this.cached &&
			Date.now() - this.lastFetch < this.getCacheDurationMs()
		) {
			return this.cached;
		}

		const result =
			(await this.fetchRemote()) ??
			(await this.loadFromCache()) ??
			({ rate: 1, asOf: "unavailable" } satisfies FxRate);

		this.cached = result;
		this.lastFetch = Date.now();
		return result;
	}
}

/** Set the logger for FX warnings. */
export function setFxLogger(logger: Logger): void {
	FxCatalogue.get().setLogger(logger);
}

/** Resolve the current USD->EUR rate (cached). */
export function getFxRate(): Promise<FxRate> {
	return FxCatalogue.get().getRate();
}
